// @flow
import React, { useMemo, createContext, useContext } from 'react'
import type { ImageWithText } from '@mission.io/mission-toolkit/actions'
import { useCreativeCanvasData } from './useCreativeCanvasData'

export type CanvasMedia = { ...ImageWithText, _id: string }
export type BackgroundCanvasMedia = { url: string, _id: string }

// The key that is used as the media type for the background image
export const BACKGROUND_IMAGE_KEY = 'BACKGROUND_IMAGE'
export const FRAME_LOCK_ID = 'CANVAS_FRAME_LOCK'
export const BACKGROUND_IMAGE_ID: string = 'CANVAS_BACKGROUND_IMAGE'

// /**
type CanvasMediaContextType = {
	backgroundImage: ?BackgroundCanvasMedia,
	images: Array<CanvasMedia>,
}

/**
 * Get any image media that will be displayed on the canvas. For now this includes background media and design media.
 * @param {ReduxStore} state
 * @returns {Array<CanvasMedia>} array of objects containing the url and type of the media
 * @returns {Array<CanvasMedia>} array of objects containing the url and type of the media
 */
function useMemoizedCanvasMedia(
	documentId: string,
	canvasId: string
): CanvasMediaContextType {
	const creativeCanvas = useCreativeCanvasData(canvasId)
	const team = creativeCanvas?.teams.find(
		team => team.document.id === documentId
	)
	const canvasData = creativeCanvas?.canvasData

	return useMemo(() => {
		let media = []
		let backgroundImage = null
		const teamSpecificCanvasData = team
			? canvasData?.issues[team.issueId]
			: null
		if (teamSpecificCanvasData?.backgroundMedia) {
			backgroundImage = {
				url: teamSpecificCanvasData.backgroundMedia.url,
				_id: teamSpecificCanvasData.backgroundMedia._id,
			}
		} else if (canvasData?.backgroundMedia) {
			backgroundImage = {
				url: canvasData.backgroundMedia.url,
				_id: canvasData.backgroundMedia._id,
			}
		}

		if (canvasData?.designMedia?.length) {
			media.push(...canvasData.designMedia)
		}
		if (teamSpecificCanvasData?.designMedia?.length) {
			media.push(...teamSpecificCanvasData.designMedia)
		}

		return { images: media, backgroundImage }
	}, [team, canvasData])
}

export const CanvasMediaContext: React$Context<CanvasMediaContextType> =
	createContext<CanvasMediaContextType>({ images: [], backgroundImage: null })

export const CanvasMediaProvider = ({
	children,
	documentId,
	canvasId,
}: {
	children: React$Node,
	documentId: string,
	canvasId: string,
}): React$Node => {
	const media = useMemoizedCanvasMedia(documentId, canvasId)
	return (
		<CanvasMediaContext.Provider value={media}>
			{children}
		</CanvasMediaContext.Provider>
	)
}

export const useCanvasMedia = (): CanvasMediaContextType => {
	return useContext(CanvasMediaContext)
}
